import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Users, Plus, Pencil, Trash2, Check, Search, X, ShieldCheck, Clock, AlertTriangle, ArrowUpRight, MailPlus, Loader2 } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsuariosPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCounts, setDeleteCounts] = useState(null);
  const [deleteCountsLoading, setDeleteCountsLoading] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [responsaveis, setResponsaveis] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "member", profile_id: "" });
  const [error, setError] = useState("");
  const appearance = useAppearance();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const tc500 = appearance.colors?.["500"] || "#06B6D4";
  const tc50 = appearance.colors?.["50"] || "#ECFEFF";

  const isAdmin = me?.role === "admin" || me?.role === "owner";
  const isOwner = me?.role === "owner";

  // Subscription user limit
  const [subData, setSubData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stripe/my-subscription`, { withCredentials: true })
      .then(res => setSubData(res.data)).catch(() => {});
  }, []);
  const maxUsers = subData?.subscription_plan?.total_users || null;
  const activeUserCount = subData?.active_users || 0;
  const atLimit = maxUsers !== null && activeUserCount >= maxUsers;

  const roleLabel = (r) => (r === "admin" || r === "owner") ? "Admin" : "Membro";

  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API}/users`, { withCredentials: true }); setUsers(res.data); } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);
  const fetchProfiles = useCallback(async () => {
    try { const res = await axios.get(`${API}/profiles`, { withCredentials: true }); setProfiles(res.data); } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { fetchUsers(); fetchProfiles(); }, [fetchUsers, fetchProfiles]);

  const openNew = () => { setEditUser(null); setForm({ name: "", email: "", role: "member", profile_id: "" }); setError(""); setDialogOpen(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ name: u.name, email: u.email, role: u.role, profile_id: u.profile_id || "" }); setError(""); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    try {
      if (editUser) {
        await axios.put(`${API}/users/${editUser.user_id}`, { name: form.name, role: form.role, profile_id: form.profile_id || null }, { withCredentials: true });
      } else {
        await axios.post(`${API}/users`, { ...form, profile_id: form.profile_id || null }, { withCredentials: true });
      }
      setDialogOpen(false); fetchUsers();
    } catch (err) { setError(err.response?.data?.detail || "Erro ao salvar usuário"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const params = transferTo ? `?transfer_to=${transferTo}` : "";
      await axios.delete(`${API}/users/${deleteTarget.user_id}${params}`);
      toast.success(`Usuário "${deleteTarget.name}" removido.`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover usuário");
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const confirmResend = async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      await axios.post(`${API}/users/${resendTarget.user_id}/resend-invite`, {}, { withCredentials: true });
      toast.success(`Convite reenviado para ${resendTarget.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reenviar convite");
    } finally {
      setResending(false);
      setResendTarget(null);
    }
  };

  const filtered = users.filter(u => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  const statusLabel = (s) => s === "pending" ? "Pendente" : "Ativo";

  return (
    <div data-testid="usuarios-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Users className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              {t("configuracoes.usuarios.title")} <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{filtered.length}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">{t("configuracoes.usuarios.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar usuário..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          {isAdmin && (
            <Tooltip><TooltipTrigger asChild><Button onClick={openNew} size="icon" style={{ backgroundColor: atLimit ? "#94a3b8" : tc500 }} className="text-white hover:opacity-90 h-9 w-9" disabled={atLimit}><Plus className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>{atLimit ? `Limite de ${maxUsers} usuário(s) atingido` : t("configuracoes.usuarios.new")}</TooltipContent></Tooltip>
          )}
        </div>
      </div>

      {!isAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
          Apenas administradores podem gerenciar usuários.
        </div>
      )}

      {/* User limit banner */}
      {maxUsers !== null && (
        <div className={`rounded-lg px-4 py-3 flex items-center justify-between ${atLimit ? "bg-rose-50 border border-rose-200" : "bg-slate-50 border border-slate-200"}`}>
          <div className="flex items-center gap-3">
            <Users className={`w-4 h-4 flex-shrink-0 ${atLimit ? "text-rose-500" : "text-slate-400"}`} />
            <div>
              <span className={`text-sm font-medium ${atLimit ? "text-rose-700" : "text-slate-700"}`}>
                {activeUserCount} de {maxUsers} usuário(s)
              </span>
              {atLimit && (
                <p className="text-xs text-rose-500 mt-0.5">Limite atingido. Faça upgrade para adicionar mais usuários.</p>
              )}
            </div>
          </div>
          {atLimit && (
            <Button
              data-testid="upgrade-users-btn"
              size="sm"
              onClick={() => navigate("/checkout")}
              className="text-white text-xs h-7 px-3"
              style={{ backgroundColor: tc500 }}
            >
              <ArrowUpRight className="w-3 h-3 mr-1" />Upgrade
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white rounded-lg border border-slate-200 animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-lg border border-slate-200">
            <Users className="w-10 h-10 mb-3 text-slate-300" />
            <p className="text-sm">{users.length === 0 ? "Nenhum usuário cadastrado." : "Nenhum usuário encontrado."}</p>
          </div>
        ) : filtered.map(u => {
          const isMe = u.user_id === me?.user_id;
          const avatarColor = u.status === "pending" ? "#94a3b8" : tc500;
          return (
            <div key={u.user_id}
              className="bg-white rounded-lg border border-slate-200 shadow-sm hover:border-slate-400 transition-colors p-4 flex items-center gap-4 cursor-pointer"
              onClick={() => isAdmin ? openEdit(u) : undefined}>

              {/* Avatar */}
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: avatarColor }}>
                <span className="text-white font-semibold text-lg">
                  {u.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{u.name}</span>
                  {isMe && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Você</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />{roleLabel(u.role)}
                  </span>
                  <span className="flex items-center gap-1">
                    {u.status === "pending" ? <Clock className="w-3 h-3 text-amber-500" /> : <Check className="w-3 h-3 text-emerald-500" />}
                    <span className={u.status === "pending" ? "text-amber-600" : "text-emerald-600"}>{statusLabel(u.status)}</span>
                  </span>
                  {u.profile_id && profiles.find(p => p.id === u.profile_id) && (
                    <span className="flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-violet-500" />
                      <span className="text-violet-600">{profiles.find(p => p.id === u.profile_id)?.name}</span>
                    </span>
                  )}
                  {!u.profile_id && u.role === "member" && (
                    <span className="text-amber-500 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />Sem perfil
                    </span>
                  )}
                  <span className="flex items-center gap-1 truncate">
                    <span>{u.email}</span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              {isAdmin && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {u.status === "pending" && (
                    <Tooltip><TooltipTrigger asChild>
                      <Button data-testid={`resend-invite-${u.user_id}`} variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setResendTarget(u); }} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                        <MailPlus className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger><TooltipContent>Reenviar convite</TooltipContent></Tooltip>
                  )}
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openEdit(u); }} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger><TooltipContent>{t("common.edit")}</TooltipContent></Tooltip>
                  {!isMe && (
                    <Tooltip><TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={async (e) => {
                        e.stopPropagation();
                        setDeleteTarget(u);
                        setTransferTo("");
                        setDeleteCounts(null);
                        setDeleteCountsLoading(true);
                        try {
                          const [countsRes, respRes] = await Promise.all([
                            axios.get(`${API}/users/${u.user_id}/counts`),
                            axios.get(`${API}/users/responsaveis`),
                          ]);
                          setDeleteCounts(countsRes.data);
                          setResponsaveis(respRes.data.filter(r => r.user_id !== u.user_id));
                        } catch { setDeleteCounts({ deals: 0, tasks: 0 }); }
                        setDeleteCountsLoading(false);
                      }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger><TooltipContent>{t("ui.remover")}</TooltipContent></Tooltip>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color: tc500 }} />
              {editUser ? "Editar Usuário" : "Novo Usuário"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label className="text-slate-700">{t("ui.nome_completo")}</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              {!editUser && (
                <div>
                  <Label className="text-slate-700">E-mail</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                  <p className="text-xs text-slate-400 mt-1">Um convite sera enviado para este e-mail.</p>
                </div>
              )}
              {editUser && (
                <div>
                  <Label className="text-slate-500 text-xs">Email</Label>
                  <p className="text-sm text-slate-500 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">{form.email}</p>
                </div>
              )}
              {/* Role selector */}
              {isOwner ? (
                <div>
                  <Label className="text-slate-700">{t("ui.permissao")}</Label>
                  <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{t("ui.role_admin")}</SelectItem>
                      <SelectItem value="member">Membro — acesso ao CRM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <ShieldCheck className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Será cadastrado como <strong>Membro</strong></span>
                </div>
              )}
              {/* Profile selector */}
              <div>
                <Label className="text-slate-700">Perfil de acesso</Label>
                <Select value={form.profile_id || "none"} onValueChange={v => setForm({...form, profile_id: v === "none" ? "" : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar perfil..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem perfil (acesso bloqueado para membros)</SelectItem>
                    {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.profile_id && <p className="text-xs text-amber-600 mt-1">Membro sem perfil não conseguirá fazer login.</p>}
              </div>
            </div>
            {error && <p className="text-xs text-rose-500 bg-rose-50 rounded px-3 py-2">{error}</p>}
            <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90">
              <Check className="w-4 h-4 mr-2" />{editUser ? "Salvar" : "Cadastrar Usuário"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?</p>
                {deleteCountsLoading ? (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />Verificando negócios e tarefas...
                  </div>
                ) : deleteCounts && (deleteCounts.deals > 0 || deleteCounts.tasks > 0) ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                      Este usuário possui <strong>{deleteCounts.deals} negócio(s)</strong> e <strong>{deleteCounts.tasks} tarefa(s)</strong> atribuídos. Selecione para quem transferir:
                    </div>
                    <Select value={transferTo} onValueChange={setTransferTo}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Selecionar responsável..." /></SelectTrigger>
                      <SelectContent>
                        {responsaveis.map(r => (
                          <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : deleteCounts ? (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
                    Este usuário não possui negócios ou tarefas atribuídos. Pode ser removido com segurança.
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting || deleteCountsLoading || (deleteCounts && (deleteCounts.deals > 0 || deleteCounts.tasks > 0) && !transferTo)}
              className="bg-rose-600 hover:bg-rose-700 text-white">
              {deleting ? "Removendo..." : "Remover usuário"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resendTarget} onOpenChange={(v) => !v && setResendTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reenviar convite?</AlertDialogTitle>
            <AlertDialogDescription>
              O convite será reenviado para <strong>{resendTarget?.email}</strong>. O usuário receberá um novo e-mail com o link de acesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-resend-btn" onClick={confirmResend} disabled={resending} style={{ backgroundColor: tc500 }}>
              {resending ? "Enviando..." : "Sim, reenviar convite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
